import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/lib/api';
import type { ApiError } from '@/lib/api';
import { chatApiUrl } from '@/lib/chatApiUrl';

export interface Conversation {
  _id: string;
  messagePreview: string;
  threadId: string;
  userId: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
  pathway: string;
}

export interface Summary {
  conversation_id: string;
  last_summarized_index: string;
  summary: string;
  style?: SummaryStyle;
  /** Styles already generated and cached server-side */
  formats?: Partial<Record<SummaryStyle, boolean>>;
  _id: string;
}

export type SummaryStyle = 'narrative' | 'checklist' | 'bullets' | 'email';

interface GroupedConversations {
  [key: string]: Conversation[];
}

type GroupedConversationResponse = {
  pinned: Conversation[];
  today: Conversation[];
  yesterday: Conversation[];
  last7Days: Conversation[];
  thisMonth: Conversation[];
  months: GroupedConversations;
  years: GroupedConversations;
};

interface ConversationState {
  createResponse?: Conversation;
  getSummaryResponse: Summary | null;
  data: GroupedConversationResponse;
  loading: boolean;
  summaryLoading?: boolean;
  error: string | null;
}

const initialState: ConversationState = {
  data: {
    pinned: [],
    today: [],
    yesterday: [],
    last7Days: [],
    thisMonth: [],
    months: {},
    years: {},
  },
  getSummaryResponse: null,
  loading: false,
  summaryLoading: false,
  error: null,
};

export const createConversation = createAsyncThunk<
  Conversation,
  { messagePreview: string },
  { rejectValue: string }
>('conversation/create', async ({ messagePreview }, { rejectWithValue }) => {
  try {
    const response = await api.post<Conversation>(
      `${process.env.NEXT_PUBLIC_API_URL}/conversations/create`,
      { messagePreview }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to create conversation'
    );
  }
});

export const getConversations = createAsyncThunk<
  GroupedConversationResponse,
  string | undefined,
  { rejectValue: string }
>('conversation/fetchAll', async (search, { rejectWithValue }) => {
  try {
    const params = search ? { search } : undefined;
    const response = await api.get<GroupedConversationResponse>(
      `${process.env.NEXT_PUBLIC_API_URL}/conversations/list`,
      { params }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to fetch conversations'
    );
  }
});

export const deleteConversation = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('conversation/delete', async (conversationId, { rejectWithValue }) => {
  try {
    await api.delete(
      `${process.env.NEXT_PUBLIC_API_URL}/conversations/delete/${conversationId}`
    );
    return conversationId;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to delete conversation'
    );
  }
});

export const pinConversation = createAsyncThunk<
  Conversation,
  { id: string; pinned: boolean },
  { rejectValue: string }
>('conversation/pin', async ({ id, pinned }, { rejectWithValue }) => {
  try {
    const response = await api.patch<Conversation>(
      `${process.env.NEXT_PUBLIC_API_URL}/conversations/${id}/pin`,
      { pinned }
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to pin conversation'
    );
  }
});

export const updateMessagePreview = createAsyncThunk<
  Conversation,
  { id: string; messagePreview: string },
  { rejectValue: string }
>(
  'conversation/updateMessagePreview',
  async ({ id, messagePreview }, { rejectWithValue }) => {
    try {
      const response = await api.put<Conversation>(
        `${process.env.NEXT_PUBLIC_API_URL}/conversations/update-message/${id}`,
        { messagePreview }
      );
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to update message preview'
      );
    }
  }
);

export const deleteAllConversations = createAsyncThunk<
  void,
  { referenceHistory: boolean; referenceMemories: boolean },
  { rejectValue: string }
>(
  'conversation/deleteAll',
  async ({ referenceHistory, referenceMemories }, { rejectWithValue }) => {
    try {
      await api.delete(`${process.env.NEXT_PUBLIC_API_URL}/conversations/all`, {
        data: { referenceHistory, referenceMemories },
      });
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to delete all conversations'
      );
    }
  }
);

export const getSummary = createAsyncThunk<
  Summary,
  { id: string; style: SummaryStyle },
  { rejectValue: string }
>('conversation/summary', async ({ id, style }, { rejectWithValue }) => {
  try {
    const response = await api.get<{ data: Summary }>(
      chatApiUrl(`/summary/${id}?style=${style}`)
    );
    return response.data.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to get summary'
    );
  }
});

export const generateSummary = createAsyncThunk<
  Summary,
  { id: string; style: SummaryStyle },
  { rejectValue: string }
>(
  'conversation/generate/summary',
  async ({ id, style }, { rejectWithValue }) => {
    try {
      const response = await api.post<{ data: Summary }>(
        chatApiUrl(`/summary/${id}`),
        { style }
      );
      return response.data.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to get summary'
      );
    }
  }
);

export const regenerateSummary = createAsyncThunk<
  Summary,
  { id: string; style: SummaryStyle },
  { rejectValue: string }
>(
  'conversation/regenerateSummary',
  async ({ id, style }, { rejectWithValue }) => {
    try {
      const response = await api.post<{ data: Summary }>(
        chatApiUrl(`/re-generate-summary/${id}`),
        { style }
      );
      return response.data.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to regenerate summary'
      );
    }
  }
);

export const updateSummary = createAsyncThunk<
  Summary,
  { id: string; summary: string },
  { rejectValue: string }
>(
  'conversation/updateSummary',
  async ({ id, summary }, { rejectWithValue }) => {
    try {
      const response = await api.put<{ data: Summary }>(
        `${process.env.NEXT_PUBLIC_API_URL}/conversations/summary/${id}`,
        { summary }
      );
      return response.data.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to update summary'
      );
    }
  }
);

export const shareSummary = createAsyncThunk<
  { message: string },
  { conversationId: string; username: string; email?: string },
  { rejectValue: string }
>(
  'conversation/shareSummary',
  async ({ conversationId, username, email }, { rejectWithValue }) => {
    try {
      const response = await api.post<{ message: string }>(
        `${process.env.NEXT_PUBLIC_API_URL}/conversations/share/${conversationId}`,
        { username, email }
      );
      return response.data;
    } catch (error: unknown) {
      const err = error as ApiError;
      return rejectWithValue(
        err.response?.data?.message || 'Failed to share summary'
      );
    }
  }
);

const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    clearConversations: (state) => {
      state.data = {
        pinned: [],
        today: [],
        yesterday: [],
        last7Days: [],
        thisMonth: [],
        months: {},
        years: {},
      };
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(deleteConversation.rejected, (state, action) => {
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(createConversation.fulfilled, (state, action) => {
        state.createResponse = action.payload;
      })
      .addCase(createConversation.rejected, (state, action) => {
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(getSummary.fulfilled, (state, action) => {
        state.getSummaryResponse = action.payload;
        state.summaryLoading = false;
      })
      .addCase(getSummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(getSummary.rejected, (state, action) => {
        state.error = action.payload ?? 'Unknown error';
        state.summaryLoading = false;
      })
      .addCase(generateSummary.fulfilled, (state, action) => {
        state.getSummaryResponse = action.payload;
        state.summaryLoading = false;
      })
      .addCase(generateSummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(generateSummary.rejected, (state, action) => {
        state.error = action.payload ?? 'Unknown error';
        state.summaryLoading = false;
      })
      .addCase(regenerateSummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(regenerateSummary.fulfilled, (state, action) => {
        state.getSummaryResponse = action.payload;
        state.summaryLoading = false;
      })
      .addCase(regenerateSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.error = action.payload ?? 'Failed to regenerate summary';
      })
      .addCase(updateSummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(updateSummary.fulfilled, (state, action) => {
        state.getSummaryResponse = action.payload;
        state.summaryLoading = false;
      })
      .addCase(updateSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.error = action.payload ?? 'Failed to update summary';
      })
      .addCase(deleteAllConversations.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAllConversations.fulfilled, (state) => {
        state.loading = false;
        state.data = initialState.data;
      })
      .addCase(deleteAllConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to delete all conversations';
      });
  },
});

export const { clearConversations } = conversationSlice.actions;
export default conversationSlice.reducer;
